import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Modal, Form, Input, Select, message } from "antd";
import { FaDownload, FaPlay } from "react-icons/fa";
import { IoPlay } from "react-icons/io5";
import OptimizedImage from "../../commonComponents/OptimizedImage";
import detailThumb from "../../assets/images/detail-thumb.webp";
import moment from "moment";
import axios from "axios";
import {
  USER_MANAGEMENT,
  USER_MANAGEMENT_API_KEY,
  USER_MANAGEMENT_API_SECRET_KEY,
} from "../../envConstants";
import { getAppName } from "../../utils/appConfig";
import tatvaAppLogo from "../../assets/images/TatvaPracticecolor.png";
import zymedAppLogo from "../../assets/images/zymedelite.svg";

const getActionText = (contentType: string) => {
  if (!contentType) return "Read";
  
  const normalizedType = contentType.toLowerCase().trim();
  switch (normalizedType) {
    // New format-based content types
    case "videos":
    case "video":
      return "Watch";
    case "documents":
    case "document":
      return "Read";
    case "podcasts":
    case "podcast":
      return "Listen";
    case "slides":
    case "slide":
      return "Read";
    
    // Legacy content type cases for backward compatibility
    case "clinical education":
      return "Read";
    case "guidelines & protocols":
      return "Read";
    case "medical news":
      return "Read";
    case "patient case studies":
      return "Read";
    case "patient information material":
      return "Read";
    case "procedures & surgeries":
      return "Watch";
    case "recent clinical advances":
      return "Read";
    case "webinars & panel discussions":
      return "Watch";
    case "editorials & research case studies":
      return "Read";
    case "infographics":
    case "medical slide library":
      return "Read";
    case "expert opinions":
      return "Watch";
    default:
      return "Read";
  }
};

const formatDuration = (duration: string) => {
  if (!duration || typeof duration !== 'string') return "";
  
  const [hours, minutes] = duration.split(":");
  const h = parseInt(hours) || 0;
  const m = parseInt(minutes) || 0;

  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  if (m > 0) return `${m}m`;
  return "";
};

const getDurationText = (contentPreview: any) => {
  if (!contentPreview) return "";
  const fileExtension = contentPreview?.uploadContentUrl?.split(".").pop()?.toLowerCase();
  const isDocumentType = fileExtension === "pdf" || fileExtension === "ppt" || fileExtension === "pptx";
  const isSlideContent = contentPreview?.contentType?.toLowerCase().includes("slide");
  const timeToShow = (isDocumentType || isSlideContent) ? contentPreview?.readTime : contentPreview?.duration;
  if (!timeToShow) return "";
  if (typeof timeToShow === 'string' && (timeToShow.includes('h') || timeToShow.includes('m'))) {
    return timeToShow;
  }
  if (typeof timeToShow === 'string' && timeToShow.includes(':')) {
    return formatDuration(timeToShow);
  }
  return formatDuration(timeToShow);
};

// This would be a public API endpoint that doesn't require authentication
// You'll need to create this endpoint on your backend
const getPublicContentPreview = async (
  contentId: string,
  contentType: string
) => {
  try {
    // Replace with your actual public API endpoint
    const response = await axios.get(
      `${USER_MANAGEMENT}/content-management/preview/metadata?contentId=${contentId}&type=${contentType}&country=1`,
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": USER_MANAGEMENT_API_KEY,
          "x-api-secret": USER_MANAGEMENT_API_SECRET_KEY,
        },
      }
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    console.error("Error fetching public content:", error);
  }
  return null;
};

export default function PublicContentPreview() {
  const { appName, contentType, contentId } = useParams<{
    appName: string;
    contentType: string;
    contentId: string;
  }>();

  const [contentPreview, setContentPreview] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm] = Form.useForm();
  const [submittingRequest, setSubmittingRequest] = useState(false);

  // Configure message styling
  useEffect(() => {
    message.config({
      top: 100,
      duration: 4,
      maxCount: 1,
      prefixCls: "public-preview-message", // Use custom prefix to avoid conflicts
    });
  }, []);

  console.log("appName", appName, contentType, contentId);

  useEffect(() => {
    if (contentId) {
      fetchContentPreview();
    }
  }, [contentId]);

  const fetchContentPreview = async () => {
    setLoading(true);
    const preview = await getPublicContentPreview(contentId!, contentType!);
    setContentPreview(preview);
    setLoading(false);
  };

  const handleDownloadApp = () => {
    if (appName === "ZyMedElite") {
      // Redirect to app store or direct users to sign up
      // You can detect the platform and redirect accordingly
      if (/Android/i.test(navigator.userAgent)) {
        window.open("https://play.google.com/store/apps/your-app", "_blank");
      } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        window.open("https://apps.apple.com/app/your-app", "_blank");
      } else {
        // For web, redirect to sign up page
        window.open("/on-boarding", "_blank");
      }
    } else {
      window.open("/download", "_blank");
    }
  };

  const handleLogin = () => {
    if (appName === "ZyMedElite") {
      // If it's Zymed app, redirect to app store
      if (/Android/i.test(navigator.userAgent)) {
        window.open("https://play.google.com/store/apps/your-app", "_blank");
      } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        window.open("https://apps.apple.com/app/your-app", "_blank");
      } else {
        // For desktop, still redirect to app store or show download options
        window.open("https://play.google.com/store/apps/your-app", "_blank");
      }
    } else {
      // For regular web users, navigate to login page
      window.open("/on-boarding", "_blank");
    }
  };

  const handleRequestAccess = () => {
    setShowRequestModal(true);
  };

  const handleRequestSubmit = async (values: any) => {
    setSubmittingRequest(true);
    try {
      const requestData = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        mblNo: values.phoneNumber,
        registrationNo: values.registrationNumber,
        contentTitle: contentPreview?.contentTitle || "",
        contentType: contentPreview?.contentType || "",
        contentUrl: window.location.href,
        country: values.country,
      };

      const response = await axios.post(
        `${USER_MANAGEMENT}/user/request-access`,
        requestData,
        {
          headers: {
            accept: "application/json, text/plain, */*",
            "content-type": "application/json",
            origin: window.location.origin,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        message.success(
          "Access request submitted successfully! We will review and get back to you."
        );
        setShowRequestModal(false);
        requestForm.resetFields();
      }
    } catch (error) {
      console.error("Error submitting request:", error);
      message.error("Failed to submit request. Please try again.");
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleModalClose = () => {
    setShowRequestModal(false);
    requestForm.resetFields();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!contentPreview) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Content Not Found!</h2>
          <Button onClick={handleDownloadApp} type="primary" size="large">
            Download {getAppName()} App
          </Button>
        </div>
      </div>
    );
  }

  const getContentTypeIcon = () => {
    const fileExtension = contentPreview?.uploadContentUrl
      ?.split(".")
      .pop()
      ?.toLowerCase();
    if (fileExtension === "mp4")
      return <IoPlay className="text-white" size={24} />;
    if (fileExtension === "mp3")
      return <FaPlay className="text-white" size={20} />;
    if (fileExtension === "pdf")
      return <FaDownload className="text-white" size={20} />;
    return <IoPlay className="text-white" size={24} />;
  };

  const getAppLogo = () => {
    if (appName === "ZyMedElite") {
      return zymedAppLogo;
    }
    return tatvaAppLogo;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <style>
        {`
          .public-preview-message {
            z-index: 10001 !important;
            top: 100px !important;
          }
          .public-preview-message-notice {
            background: white !important;
          }
          .public-preview-message-notice-content {
            background: #f6ffed !important;
            border: 1px solid #b7eb8f !important;
            color: #389e0d !important;
            border-radius: 8px !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
            padding: 16px 20px !important;
          }
          .public-preview-message-success .anticon {
            color: #52c41a !important;
            margin-right: 8px !important;
          }
          .public-preview-message-notice-wrapper {
            background: transparent !important;
          }
        `}
      </style>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={getAppLogo()} alt="TatvaPedia" className="h-8" />
          {appName !== "ZyMedElite" && (
            <Button
              onClick={handleDownloadApp}
              type="primary"
              size="large"
              className="bg-blue-600 border-blue-600"
            >
              Get App
            </Button>
          )}
        </div>
      </div>

      {/* Content Preview */}
      <div className="max-w-4xl mx-auto">
        {/* Hero Section */}
        <div className="relative">
          <OptimizedImage
            src={contentPreview?.coverPhotoUrl || detailThumb}
            alt={contentPreview?.contentTitle}
            className="w-full h-auto object-cover"
            fallbackSrc={detailThumb}
            useIntersectionObserver={false}
          />

          {/* Overlay for video/audio content */}
          {(contentPreview?.uploadContentUrl?.includes(".mp4") ||
            contentPreview?.uploadContentUrl?.includes(".mp3")) && (
            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
              <div className="bg-blue-600 rounded-full p-4">
                {getContentTypeIcon()}
              </div>
            </div>
          )}
        </div>
        <div className="space-y-3 border-b border-gray-200 pb-4">
          {/* Login and Request Access Buttons */}
          <div className="flex gap-4 justify-center mt-4">
            {appName !== "ZyMedElite" && (
              <Button
                onClick={handleLogin}
                type="primary"
                size="large"
                className="bg-blue-600 border-blue-600 px-8"
              >
                Login
              </Button>
            )}

            {appName === "ZyMedElite" && (
              <Button
                onClick={handleRequestAccess}
                size="large"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8"
              >
                Request Access
              </Button>
            )}
          </div>
        </div>

        {/* Content Details */}
        <div className="bg-white p-4">
          {/* Content Type Badge */}
          <div className="mb-2 bg-white bg-opacity-90 py-1 rounded-full text-sm font-medium text-[#625616]">
            {contentPreview?.contentType}
          </div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
            {contentPreview?.contentTitle}
          </h1>

          <div className="text-gray-600 text-sm mb-4">
            {moment(contentPreview?.publishDate).format("DD MMM YYYY")}
            {/* {getDurationText(contentPreview) && (
              <>
                {" • "}
                {getDurationText(contentPreview)} {getActionText(contentPreview?.contentType)}
              </>
            )} */}
          </div>

          {/* Content Preview */}
          <div className="text-gray-700 text-base leading-relaxed mb-6">
            {contentPreview?.contentDescription}
          </div>

          {/* Speaker/Host Info */}
          {contentPreview?.kolSpeaker?.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Speaker:</h3>
              <div className="text-gray-700">
                <div className="font-medium">
                  {contentPreview.kolSpeaker[0].name}
                </div>
                <div className="text-sm text-gray-600">
                  {contentPreview.kolSpeaker[0].designation} |{" "}
                  {contentPreview.kolSpeaker[0].qualification}
                </div>
                <div className="text-sm text-gray-600">
                  {contentPreview.kolSpeaker[0].organization}
                </div>
              </div>
            </div>
          )}

          <div
            className={`flex items-center ${
              contentPreview?.kolSpeaker?.length > 0 && "pt-2"
            }`}
          >
            <div className="text-[14px] text-[#5A6774] mr-2">Hosted by:</div>
            <img
              loading="lazy"
              src={getAppLogo()}
              style={{ height: 29 }}
              alt={contentPreview?.kolSpeaker?.[0]?.imageName}
            />
          </div>
        </div>
      </div>

      {/* Request Access Modal */}
      <Modal
        title={
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              Request Access
            </h2>
            <p className="text-sm text-gray-500">
              Please provide the following details to access this premium
              content
            </p>
          </div>
        }
        open={showRequestModal}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        className="request-access-modal"
        styles={{
          content: { padding: 0, borderRadius: "16px", overflow: "hidden" },
          header: { padding: 0, margin: 0, borderBottom: "none" },
        }}
      >
        <div className="px-6 py-4">
          <Form
            form={requestForm}
            layout="vertical"
            onFinish={handleRequestSubmit}
            className="space-y-2"
            requiredMark={false}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-3">
              <Form.Item
                label={
                  <span className="text-sm font-semibold text-gray-800">
                    Email Address <span className="text-red-500">*</span>
                  </span>
                }
                name="email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input
                  placeholder="Enter your email address"
                  className="h-12 rounded-xl border-gray-200 shadow-sm hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className="text-sm font-semibold text-gray-800">
                    Country <span className="text-red-500">*</span>
                  </span>
                }
                name="country"
                initialValue={3}
                rules={[
                  { required: true, message: "Please select your country" },
                ]}
              >
                <Select
                  placeholder="Select your country"
                  className="premium-select"
                  size="large"
                  options={[
                    { value: 1, label: "India (+91)" },
                    { value: 2, label: "South Africa (+27)" },
                    { value: 3, label: "Philippines (+63)" },
                  ]}
                  style={{ height: "48px" }}
                />
              </Form.Item>
            </div>

            <Form.Item
              label={
                <span className="text-sm font-semibold text-gray-800">
                  Phone Number <span className="text-red-500">*</span>
                </span>
              }
              name="phoneNumber"
              rules={[
                { required: true, message: "Please enter your phone number" },
                {
                  pattern: /^\d{10,15}$/,
                  message: "Please enter a valid phone number",
                },
              ]}
            >
              <Input
                placeholder="Enter your phone number"
                className="h-12 rounded-xl border-gray-200 shadow-sm hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-200 mb-4"
              />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-3">
              <Form.Item
                label={
                  <span className="text-sm font-semibold text-gray-800">
                    First Name <span className="text-red-500">*</span>
                  </span>
                }
                name="firstName"
                rules={[
                  { required: true, message: "Please enter your first name" },
                ]}
              >
                <Input
                  placeholder="Enter your first name"
                  className="h-12 rounded-xl border-gray-200 shadow-sm hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
                />
              </Form.Item>

              <Form.Item
                label={
                  <span className="text-sm font-semibold text-gray-800">
                    Last Name <span className="text-red-500">*</span>
                  </span>
                }
                name="lastName"
                rules={[
                  { required: true, message: "Please enter your last name" },
                ]}
              >
                <Input
                  placeholder="Enter your last name"
                  className="h-12 rounded-xl border-gray-200 shadow-sm hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
                />
              </Form.Item>
            </div>

            <Form.Item
              label={
                <span className="text-sm font-semibold text-gray-800">
                  Registration Number <span className="text-red-500">*</span>
                </span>
              }
              name="registrationNumber"
              rules={[
                {
                  required: true,
                  message: "Please enter your registration number",
                },
              ]}
            >
              <Input
                placeholder="Enter your registration number"
                className="h-12 rounded-xl border-gray-200 shadow-sm hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-200"
              />
            </Form.Item>

            <div className="flex gap-4 pt-2">
              <Button
                onClick={handleModalClose}
                className="flex-1 h-12 rounded-xl border-2 border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-700 font-medium transition-all duration-200"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submittingRequest}
                className="flex-1 h-12 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 border-0 hover:from-blue-700 hover:to-indigo-700 font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]"
              >
                {submittingRequest ? "Submitting Request..." : "Submit Request"}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>
    </div>
  );
}
